// Cart service using Shopify Storefront API for headless commerce
// This handles cart operations for a headless setup

// Storefront API endpoint
const STOREFRONT_API_URL = `https://${process.env.GATSBY_MYSHOPIFY_URL}/api/2024-01/graphql.json`

// GraphQL queries for cart operations
const CART_CREATE = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  priceV2 {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

const CART_LINES_ADD = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  priceV2 {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

const CART_LINES_UPDATE = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  priceV2 {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

const CART_QUERY = `
  query getCart($cartId: ID!) {
    cart(id: $cartId) {
      id
      checkoutUrl
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                priceV2 {
                  amount
                  currencyCode
                }
                product {
                  title
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      cost {
        subtotalAmount {
          amount
          currencyCode
        }
        totalAmount {
          amount
          currencyCode
        }
      }
    }
  }
`

// Helper function to make GraphQL requests
const shopifyFetch = async (query, variables = {}) => {
  const response = await fetch(STOREFRONT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  
  if (data.errors) {
    throw new Error(data.errors[0].message)
  }

  return data.data
}

// Get current cart (this will be handled by the context)
export const getCart = async () => {
  // This will be called by the context to initialize cart
  return null
}

// Add items to cart
export const addToCart = async (cartId, variantId, quantity) => {
  try {
    const data = await shopifyFetch(CART_LINES_ADD, {
      cartId: cartId,
      lines: [
        {
          merchandiseId: variantId,
          quantity: parseInt(quantity, 10),
        },
      ],
    })

    if (data.cartLinesAdd.userErrors && data.cartLinesAdd.userErrors.length > 0) {
      throw new Error(data.cartLinesAdd.userErrors[0].message)
    }

    return data.cartLinesAdd.cart
  } catch (error) {
    console.error('Error adding to cart:', error)
    throw error
  }
}

// Update cart items
export const updateCart = async (cartId, lineItemId, quantity) => {
  try {
    const data = await shopifyFetch(CART_LINES_UPDATE, {
      cartId: cartId,
      lines: [
        {
          id: lineItemId,
          quantity: parseInt(quantity, 10),
        },
      ],
    })

    if (data.cartLinesUpdate.userErrors && data.cartLinesUpdate.userErrors.length > 0) {
      throw new Error(data.cartLinesUpdate.userErrors[0].message)
    }

    return data.cartLinesUpdate.cart
  } catch (error) {
    console.error('Error updating cart:', error)
    throw error
  }
}

// Create new cart
export const createCart = async () => {
  try {
    const data = await shopifyFetch(CART_CREATE, { input: {} })
    
    if (data.cartCreate.userErrors && data.cartCreate.userErrors.length > 0) {
      throw new Error(data.cartCreate.userErrors[0].message)
    }

    return data.cartCreate.cart
  } catch (error) {
    console.error('Error creating cart:', error)
    throw error
  }
}

// Get cart by ID
export const getCartById = async (cartId) => {
  try {
    // Validate cart ID format
    if (!cartId || !cartId.startsWith('gid://')) {
      throw new Error('Invalid cart ID format. Expected gid:// format.')
    }
    
    const data = await shopifyFetch(CART_QUERY, { cartId })
    return data.cart
  } catch (error) {
    console.error('Error fetching cart:', error)
    throw error
  }
}
